import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, Input, Space, Button, Select } from 'antd'
import { SearchOutlined, PlusOutlined } from '@ant-design/icons'
import { useMerchants } from '../hooks/useMerchants'
import { useMerchantActions } from '../hooks/useMerchantActions'
import { MerchantsTable } from '../components/MerchantsTable'
import { MerchantFormModal } from '../components/MerchantFormModal'
import { BulkActionToolbar } from '../../../shared/components/BulkActionToolbar'
import { useAuthStore } from '../../../auth/store/auth-store'

const { Option } = Select

export const MerchantsScreen: React.FC = () => {
    const {
        merchants,
        totalMerchants,
        searchText, setSearchText,
        statusFilter, setStatusFilter,
        kycFilter, setKycFilter,
        sourceFilter, setSourceFilter,
        page, setPage,
        pageSize, setPageSize,
        isLoading
    } = useMerchants()

    const { hasPermission } = useAuthStore()
    const canVerifyKyc = hasPermission('merchants.verify')

    const {
        isFormModalOpen,
        handleAddMerchant,
        handleVerifyKYC,
        handleRejectMerchant,
        handleUnblockMerchant,
        handleBulkVerify: handleBulkVerifyAction,
        handleBulkReject: handleBulkRejectAction,
        handleFormSubmit,
        closeFormModal,
        isSubmitting
    } = useMerchantActions()

    const navigate = useNavigate()

    // Override the handleViewDetails to navigate instead of opening drawer
    const handleViewDetails = (merchant: any) => {
        navigate(`/merchants/${merchant.merchant_id}`)
    }

    const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>([])

    const handleSelectionChange = (newSelectedRowKeys: React.Key[]) => {
        setSelectedRowKeys(newSelectedRowKeys)
    }

    const handleClearSelection = () => {
        setSelectedRowKeys([])
    }

    const handleBulkVerify = () => {
        handleBulkVerifyAction(selectedRowKeys, () => setSelectedRowKeys([]))
    }

    const handleBulkReject = () => {
        handleBulkRejectAction(selectedRowKeys, () => setSelectedRowKeys([]))
    }

    return (
        <div style={{ width: '100%' }}>
            <Card>
                <Space style={{ marginBottom: 16 }}>
                    <Input
                        placeholder="Search merchants..."
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ width: 250 }}
                    />
                    <Select
                        placeholder="Filter by source"
                        style={{ width: 150 }}
                        value={sourceFilter}
                        onChange={setSourceFilter}
                    >
                        <Option value="all">All Sources</Option>
                        <Option value="admin">Admin Created</Option>
                        <Option value="self">Self Registered</Option>
                    </Select>
                    <Select
                        placeholder="Filter by status"
                        style={{ width: 150 }}
                        value={statusFilter}
                        onChange={setStatusFilter}
                    >
                        <Option value="all">All Status</Option>
                        <Option value="onboarding_pending">Onboarding</Option>
                        <Option value="pending">Pending Approval</Option>
                        <Option value="active">Active</Option>
                        <Option value="suspended">Suspended</Option>
                        <Option value="blocked">Blocked</Option>
                    </Select>
                    <Select
                        placeholder="Filter by KYC"
                        style={{ width: 150 }}
                        value={kycFilter}
                        onChange={setKycFilter}
                    >
                        <Option value="all">All KYC</Option>
                        <Option value="pending">Pending</Option>
                        <Option value="approved">Approved</Option>
                    </Select>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAddMerchant}>
                        Add Merchant
                    </Button>
                </Space>

                <MerchantsTable
                    merchants={merchants}
                    loading={isLoading}
                    pagination={{
                        current: page,
                        pageSize: pageSize,
                        total: totalMerchants,
                        onChange: (p, ps) => {
                            setPage(p)
                            setPageSize(ps)
                        }
                    }}
                    onRowClick={handleViewDetails}
                    onVerifyKyc={handleVerifyKYC}
                    onReject={handleRejectMerchant}
                    onUnblock={handleUnblockMerchant}
                    selectedRowKeys={selectedRowKeys}
                    onSelectionChange={handleSelectionChange}
                    canVerifyKyc={canVerifyKyc}
                />
            </Card>

            <BulkActionToolbar
                selectedCount={selectedRowKeys.length}
                onClearSelection={handleClearSelection}
                actions={
                    <>
                        {canVerifyKyc && (
                            <>
                                <Button type="primary" onClick={handleBulkVerify}>Verify Selected</Button>
                                <Button danger onClick={handleBulkReject}>Reject Selected</Button>
                            </>
                        )}
                    </>
                }
            />

            <MerchantFormModal
                open={isFormModalOpen}
                onCancel={closeFormModal}
                onSubmit={handleFormSubmit}
                isSubmitting={isSubmitting}
            />
        </div>
    )
}
